import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { DataTable, EmptyState, PageHeader, StatCard, type DataTableProps } from '@routes-plan/ui';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import PermissionButton from '../../../components/common/PermissionButton';
import { useT } from '../../../i18n';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

/**
 * Piezas comunes de los reportes. En las rutas /reports/ el contenedor (Wrapper) fija la altura de la
 * página: el reporte ocupa ese alto, con el encabezado y la tarjeta de filtros arriba y los resultados
 * llenando el resto. La tabla se desplaza dentro de su área; si en un teléfono no cabe todo, el reporte
 * se desplaza hacia abajo (nunca a los lados).
 */

interface ReportPageProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: React.ComponentProps<typeof PageHeader>['breadcrumbs'];
  actions?: React.ReactNode;
  /** Ayuda junto al título; por defecto, el «?» del recorrido de la pantalla (si tiene). */
  helpAction?: React.ReactNode;
  children: React.ReactNode;
}

export const ReportPage: React.FC<ReportPageProps> = ({ title, description, breadcrumbs, actions, helpAction = <ScreenTourButton />, children }) => (
  <Box
    sx={{
      flex: '1 1 auto',
      minHeight: 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      overflowY: 'auto',
      overflowX: 'hidden',
      pb: 1,
    }}
  >
    <Box sx={{ flexShrink: 0, '& > header': { mb: 0 } }}>
      <PageHeader title={title} description={description} breadcrumbs={breadcrumbs} actions={actions} helpAction={helpAction} />
    </Box>
    {children}
  </Box>
);

interface ReportFiltersProps {
  /** Al enviar (botón Consultar o Enter en un campo). */
  onSubmit: () => void;
  /** Texto del botón; por defecto "Consultar". */
  submitLabel?: React.ReactNode;
  submitDisabled?: boolean;
  /** Botones extra junto a Consultar. */
  extraActions?: React.ReactNode;
  /** Contenido debajo de la rejilla (por ejemplo, opciones que ocupan todo el ancho). */
  footer?: React.ReactNode;
  /** Atributos extra del botón Consultar (por ejemplo, `data-tour` de la guía). */
  submitButtonProps?: Record<string, unknown>;
  children: React.ReactNode;
}

/** Tarjeta de filtros: campos en rejilla y el botón para consultar al final. */
export const ReportFilters: React.FC<ReportFiltersProps> = ({ onSubmit, submitLabel, submitDisabled, extraActions, footer, submitButtonProps, children }) => {
  const t = useT();
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      sx={{ p: 2, flexShrink: 0, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            flex: '1 1 480px',
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))',
            gap: 2,
          }}
        >
          {children}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 100%', sm: '0 0 auto' }, flexWrap: 'wrap' }}>
          {extraActions}
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchOutlined />}
            disabled={submitDisabled}
            {...submitButtonProps}
            sx={{ minHeight: 40, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            {submitLabel ?? t('summaryReports.shared.query')}
          </Button>
        </Box>
      </Box>
      {footer && (
        <>
          <Divider sx={{ my: 2 }} />
          {footer}
        </>
      )}
    </Paper>
  );
};

/** Rejilla de indicadores (totales del reporte). `tourId`: marca `data-tour` para el recorrido de la pantalla. */
export const ReportStats: React.FC<{ children: React.ReactNode; tourId?: string }> = ({ children, tourId }) => (
  <Box
    data-tour={tourId}
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))',
      gap: 2,
      flexShrink: 0,
      minWidth: 0,
    }}
  >
    {children}
  </Box>
);

export const ReportStat = StatCard;

interface ReportResultsProps {
  title?: React.ReactNode;
  /** Texto a la derecha del título (por ejemplo, el periodo consultado). */
  meta?: React.ReactNode;
  children: React.ReactNode;
  /** Alto mínimo del área de resultados. */
  minHeight?: number | Record<string, number | string>;
  /** false: la tarjeta toma el alto de su contenido (para resultados cortos o varias tablas). */
  fill?: boolean;
}

/** Tarjeta de resultados: llena el alto que queda debajo de los filtros. */
export const ReportResults: React.FC<ReportResultsProps> = ({ title, meta, children, minHeight, fill = true }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      ...(fill ? { flex: '1 1 0', minHeight: minHeight ?? { xs: 520, md: 380 } } : { flexShrink: 0 }),
    }}
  >
    {(title || meta) && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
        {title && (
          <Typography variant="h6" component="h2" sx={{ minWidth: 0 }}>
            {title}
          </Typography>
        )}
        {meta && (
          <Typography variant="body2" color="text.secondary">
            {meta}
          </Typography>
        )}
      </Box>
    )}
    {children}
  </Paper>
);

/**
 * `DataTable` de la librería que llena el alto disponible de su tarjeta: la barra (búsqueda, exportar)
 * y la paginación quedan fijas y las filas se desplazan dentro del área, con el encabezado fijo.
 */
export function ReportTable<T>(props: DataTableProps<T> & { fill?: boolean }) {
  const { fill = true, ...rest } = props;
  if (!fill) return <DataTable<T> initialPageSize={50} pageSizeOptions={[25, 50, 100, 500]} {...rest} />;
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > div': { flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' },
        '& .MuiTableContainer-root': { flex: '1 1 0', minHeight: 160 },
      }}
    >
      <DataTable<T> initialPageSize={50} pageSizeOptions={[25, 50, 100, 500]} maxHeight="none" {...rest} />
    </Box>
  );
}

interface Option {
  id: number | string;
  name: string;
}

/** Selector de una sede. */
export const OfficeSelect: React.FC<{
  offices: Option[];
  value: string | number | '' | null | undefined;
  onChange: (id: string) => void;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Marca `data-tour` para el recorrido de la pantalla. */
  tourId?: string;
}> = ({ offices, value, onChange, label, required, disabled, tourId }) => {
  const t = useT();
  const id = React.useId();
  const text = label ?? t('summaryReports.shared.office');
  return (
    <FormControl fullWidth size="small" required={required} disabled={disabled} data-tour={tourId}>
      <InputLabel id={id}>{text}</InputLabel>
      <Select
        labelId={id}
        label={text}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {offices.map((o) => (
          <MenuItem key={o.id} value={String(o.id)}>
            {o.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/** Lista desplegable sencilla de un filtro. */
export const ReportSelect: React.FC<{
  options: Option[];
  value: string | number | null | undefined;
  onChange: (id: string) => void;
  label: React.ReactNode;
  /** Opción vacía al inicio (por ejemplo, "Todos"). */
  emptyOption?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Marca `data-tour` para el recorrido de la pantalla. */
  tourId?: string;
}> = ({ options, value, onChange, label, emptyOption, required, disabled, tourId }) => {
  const id = React.useId();
  return (
    <FormControl fullWidth size="small" required={required} disabled={disabled} data-tour={tourId}>
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        labelId={id}
        label={label}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {emptyOption !== undefined && <MenuItem value="">{emptyOption}</MenuItem>}
        {options.map((o) => (
          <MenuItem key={o.id} value={String(o.id)}>
            {o.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ALL = '__all__';

/**
 * Selector de varios elementos en una lista desplegable con casillas y "Todos" al inicio.
 * Trabaja con los identificadores como texto.
 */
export const CheckMultiSelect: React.FC<{
  options: Option[];
  value: string[];
  onChange: (ids: string[]) => void;
  label: React.ReactNode;
  /** Opción del inicio para marcar o desmarcar todos. */
  allOption: React.ReactNode;
  /** Texto del campo cuando están todos marcados. */
  allSelectedText: (count: number) => string;
  /** Texto del campo con varios marcados. */
  someSelectedText: (count: number) => string;
  disabled?: boolean;
  required?: boolean;
  /** Marca `data-tour` para el recorrido de la pantalla. */
  tourId?: string;
}> = ({ options, value, onChange, label, allOption, allSelectedText, someSelectedText, disabled, required, tourId }) => {
  const id = React.useId();
  const all = options.length > 0 && options.every((r) => value.includes(String(r.id)));
  const some = value.length > 0 && !all;
  return (
    <FormControl fullWidth size="small" disabled={disabled || options.length === 0} required={required} data-tour={tourId}>
      <InputLabel id={id}>{label}</InputLabel>
      <Select<string[]>
        labelId={id}
        label={label}
        multiple
        value={value}
        onChange={(e) => {
          const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          if (next.includes(ALL)) {
            onChange(all ? [] : options.map((r) => String(r.id)));
            return;
          }
          onChange(next);
        }}
        renderValue={(selected) => {
          if (all) return allSelectedText(options.length);
          if (selected.length === 1) return options.find((r) => String(r.id) === selected[0])?.name ?? '';
          return someSelectedText(selected.length);
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
      >
        <MenuItem value={ALL} dense>
          <Checkbox size="small" checked={all} indeterminate={some} sx={{ p: 0.5, mr: 1 }} />
          <ListItemText primary={allOption} primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>
        <Divider />
        {options.map((r) => (
          <MenuItem key={r.id} value={String(r.id)} dense>
            <Checkbox size="small" checked={value.includes(String(r.id))} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={r.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/** Selector de varias rutas de la sede elegida. */
export const RouteMultiSelect: React.FC<{
  routes: Option[];
  value: string[];
  onChange: (ids: string[]) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  tourId?: string;
}> = ({ routes, label, ...rest }) => {
  const t = useT();
  return (
    <CheckMultiSelect
      options={routes}
      label={label ?? t('summaryReports.shared.routes')}
      allOption={t('summaryReports.shared.selectAllRoutes')}
      allSelectedText={(count) => t('summaryReports.shared.allRoutes', { count })}
      someSelectedText={(count) => t('summaryReports.shared.routesSelected', { count })}
      {...rest}
    />
  );
};

/** Estado vacío de un reporte: antes de consultar o cuando la consulta no trae filas. */
export const ReportEmpty: React.FC<{ queried?: boolean; title?: React.ReactNode; description?: React.ReactNode }> = ({
  queried,
  title,
  description,
}) => {
  const t = useT();
  return (
    <EmptyState
      dense
      icon={<AssessmentOutlined fontSize="inherit" />}
      title={title ?? (queried ? t('summaryReports.shared.noData') : t('summaryReports.shared.emptyTitle'))}
      description={description ?? (queried ? undefined : t('summaryReports.shared.emptyHint'))}
    />
  );
};

/**
 * Acción de una fila con forma de ícono, que respeta el permiso (`PermissionButton`): sin permiso se
 * oculta o queda desactivada con su explicación.
 */
export const RowAction: React.FC<{
  allowed?: boolean;
  deniedTooltip?: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: 'primary' | 'error' | 'inherit' | 'secondary' | 'success' | 'warning' | 'info';
  disabled?: boolean;
}> = ({ allowed = true, deniedTooltip = '', label, icon, onClick, color = 'primary', disabled }) => (
  <PermissionButton
    allowed={allowed}
    deniedTooltip={deniedTooltip}
    tooltip={label}
    aria-label={label}
    variant="text"
    color={color}
    size="small"
    disabled={disabled}
    onClick={(e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{ minWidth: 0, width: 36, height: 36, p: 0, borderRadius: '50%' }}
  >
    {icon}
  </PermissionButton>
);

/** Alto de la tabla dentro de una ventana: deja ver el encabezado y el pie de la ventana. */
export const MODAL_TABLE_HEIGHT = 'min(52vh, 520px)';

/** Tabla dentro de una ventana (`StandardModal`): mismo `DataTable`, con alto para la ventana. */
export function ModalTable<T>(props: DataTableProps<T>) {
  return <DataTable<T> initialPageSize={25} pageSizeOptions={[25, 50, 100]} maxHeight={MODAL_TABLE_HEIGHT} {...props} />;
}
